<template>
  <div class="chat-screen-wrapper">
    <div
      v-if="!isXSmallScreenAndDown || showConversationList"
      id="conversation-list"
      :class="[
        'conversations-overview',
        {
          'conversations-overview--mobile': isXSmallScreenAndDown,
        },
      ]"
    >
      <div class="conversations-overview-header hidden-xs-and-down">
        <FriedH3>{{ t('dr_conversations') }}</FriedH3>
        <FriedRoundedButton
          data-external="chat-add-new-conversation-button"
          @click="handleCreateNewConversation"
        >
          <template #icon>
            <FriedIcon icon="plus-large" />
          </template>
        </FriedRoundedButton>
      </div>
      <div
        v-for="orderedConversation in orderedConversations"
        :key="orderedConversation.id"
        :class="['conversation-item', { active: isActiveConversation(orderedConversation.id) }]"
        @click="handleChangeConversation(orderedConversation.id)"
      >
        <ChatListItem
          :conversation="orderedConversation"
          :chat-participants="chatParticipants"
          :participants="participants"
          :participant-id="participantId"
          :draft-storage-key="draftStorageKey"
          :typing="typing"
          :user="owner"
          :previous-room-owners-ids="previousRoomOwnersIds"
          published-view
        />
      </div>
      <FriedButton
        v-if="isXSmallScreenAndDown && showConversationList"
        class="chat-add-new-conversation-button"
        data-external="chat-add-new-conversation-button-mobile"
        @click="handleCreateNewConversation"
      >
        {{ t('dr_chat_new_conversation') }}
        <template #rightIcon>
          <FriedIcon icon="plus-large" />
        </template>
      </FriedButton>
    </div>
    <ChatWrapper
      v-if="!isXSmallScreenAndDown || !showConversationList"
      class="chat-wrapper"
      :chat-participants="chatParticipants"
      :all-participants="allParticipants"
      :participant-id="participantId"
      :creating-new-conversation="creatingNewConversation"
      :owner-company-name="ownerCompanyName"
      :open-conversation-payload="openConversationPayload"
      :draft-storage-key="draftStorageKey"
      :loaded="loaded"
      :placement="SidebarPlacement.Full"
      :previous-room-owners-ids="previousRoomOwnersIds"
      @show-conversation-list="showConversationList = true"
    />
  </div>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { watch, nextTick, onBeforeUnmount, onMounted, computed, defineComponent, ref } from 'vue';
import type { ChatParticipant, Participant } from '@getaccept/dsr-shared-new';
import { ChatConstants, ChatHelper, SidebarPlacement } from '@getaccept/dsr-shared-new';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import ChatListItem from '@getaccept/dsr-shared-new/src/chat/components/ChatListItem.vue';
import useBreakpoints from '@getaccept/lib-shared-new/src/composables/breakpoints.composable';
import { useParticipantsStore } from '../../store/participants.store';
import { useChatPublishedStore } from '../store/chat-published.store';
import ChatWrapper from './ChatWrapper.vue';

export default defineComponent({
  name: 'Chat',
  components: {
    ChatWrapper,
    ChatListItem,
  },
  props: {
    loaded: {
      type: Boolean,
    },
    participantId: {
      required: true,
      type: String,
    },
    owner: {
      type: Object as PropType<Participant>,
      default: () => ({} as Participant),
    },
    ownerCompanyName: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { t } = useI18n();
    const participantsStore = useParticipantsStore();
    const { participants, senders } = storeToRefs(participantsStore);
    const chatStore = useChatPublishedStore();

    const { conversations, conversation, typing, openConversationPayload } = storeToRefs(chatStore);
    const creatingNewConversation = computed(() => !conversation.value);

    const allParticipants = computed(() => [...participants.value, ...senders.value]);

    const chatParticipants = computed(() => {
      const mappedSenders: ChatParticipant[] = senders.value.map(sender => ({
        user: sender,
        userId: sender.id,
      }));

      const filteredParticipants: ChatParticipant[] = participants.value
        .filter(participant => participant.id !== props.participantId)
        .map(participant => ({ participant, participantId: participant.id }));
      return [...filteredParticipants, ...mappedSenders];
    });

    const orderedConversations = computed(() =>
      ChatHelper.orderConversations([...conversations.value])
    );

    const { isXSmallScreenAndDown } = useBreakpoints();
    const showConversationList = ref(false);

    const setCreatingNewConversation = () => {
      chatStore.setCurrentConversation(null);
      chatStore.handleNewConversationParticipants([]);
    };

    const handleChangeConversation = (conversationId: string) => {
      if (isXSmallScreenAndDown.value) {
        showConversationList.value = false;
      }
      chatStore.setTypingStatus(false);
      chatStore.setCurrentConversation(conversationId);
    };

    const isActiveConversation = (conversationId: string) =>
      !isXSmallScreenAndDown.value && conversationId === conversation.value?.id;

    watch(
      openConversationPayload,
      async payload => {
        if (!payload) {
          return;
        }

        if (payload?.conversationId) {
          await nextTick();
          handleChangeConversation(payload.conversationId);
        } else {
          setCreatingNewConversation();
        }
      },
      { immediate: true }
    );

    const handleCreateNewConversation = () => {
      if (isXSmallScreenAndDown.value) {
        showConversationList.value = false;
      }
      chatStore.setOpenConversationPayload(null);
      setCreatingNewConversation();
    };

    const draftStorageKey = `${ChatConstants.draftStorageKeyPublished}_${props.participantId}`;

    const previousRoomOwnersIds = ref<string[]>([]);

    watch(
      conversations,
      () => {
        if (!conversations.value.length) {
          return;
        }

        previousRoomOwnersIds.value = ChatHelper.findAllPreviousOwnersIds(
          conversations.value,
          props.owner.id
        );
      },
      { immediate: true }
    );

    onMounted(() => {
      if (isXSmallScreenAndDown.value) {
        showConversationList.value = true;
      }
    });

    onBeforeUnmount(() => {
      chatStore.setDefaultConversation();
      chatStore.setOpenConversationPayload(null);
    });

    return {
      creatingNewConversation,
      participants,
      conversation,
      typing,
      allParticipants,
      chatParticipants,
      openConversationPayload,
      handleChangeConversation,

      setCreatingNewConversation,
      isActiveConversation,
      orderedConversations,
      SidebarPlacement,
      t,
      handleCreateNewConversation,
      isXSmallScreenAndDown,
      showConversationList,
      draftStorageKey,
      previousRoomOwnersIds,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/scss/chat-view';
</style>
